import { useState, useEffect } from 'react';
import { User } from '../types/user';

interface ClientProfile {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  dateOfBirth: string;
  gender: string;
  weight: number | null;
  height: number | null;
  isComplete: boolean;
  createdAt: string;
  updatedAt: string;
}

interface OnboardingState {
  isFirstLogin: boolean;
  hasCompleteProfile: boolean;
  profileExists: boolean;
  profile: ClientProfile | null;
  needsProfileCompletion: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useClientOnboarding = (user: User | null) => {
  const [state, setState] = useState<OnboardingState>({
    isFirstLogin: false,
    hasCompleteProfile: false,
    profileExists: false,
    profile: null,
    needsProfileCompletion: false,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    if (!user) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    checkOnboardingStatus();
  }, [user]);

  const checkOnboardingStatus = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Use the profileComplete flag from the backend instead of checking all fields
      // The backend already validates and sets this flag when all required fields are present
      const hasCompleteProfile = user?.profileComplete === true;
      const needsProfileCompletion = !hasCompleteProfile;

      // Simple logic: first login = hasn't seen welcome modal yet
      const hasSeenWelcomeModal = user?.hasSeenWelcomeModal === true;
      const isFirstLogin = !hasSeenWelcomeModal;


      setState({
        isFirstLogin,
        hasCompleteProfile: !!hasCompleteProfile,
        profileExists: true, // User exists, so profile exists (even if incomplete)
        profile: null, // We don't need a separate profile object for now
        needsProfileCompletion,
        isLoading: false,
        error: null
      });

    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }));
    }
  };

  const markProfileComplete = async (profile: ClientProfile) => {
    try {
      // Update local state
      setState(prev => ({
        ...prev,
        profile,
        hasCompleteProfile: profile.isComplete,
        needsProfileCompletion: !profile.isComplete,
        profileExists: true
      }));

      // If profile is complete and this was a first login, 
      // we should also update the user's status to active
      if (profile.isComplete && state.isFirstLogin) {
        const userUpdateResponse = await fetch(`/api/users/${user.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: 'active'
          }),
        });

        if (userUpdateResponse.ok) {
          // Profile completion process is now fully complete
          setState(prev => ({
            ...prev,
            isFirstLogin: false,
            needsProfileCompletion: false
          }));
        }
      }

    } catch (error) {
      console.error('Error updating profile completion status:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update profile status'
      }));
    }
  };

  const skipProfileCompletion = () => {
    // Skip only dismisses modals for this session
    // Profile completion status should remain based on actual data
    // Don't modify needsProfileCompletion - it should reflect reality
    setState(prev => ({
      ...prev,
      isFirstLogin: false  // Only prevent welcome modal from showing again
    }));
  };

  const retryOnboardingCheck = () => {
    if (user) {
      checkOnboardingStatus();
    }
  };

  return {
    ...state,
    markProfileComplete,
    skipProfileCompletion,
    retryOnboardingCheck
  };
};